// Next Diamonds — Carat Weight Selector
// Click a pill →
//   1. Updates the disclaimer line
//   2. Updates the live "Carat Weight" cell in the spec table
//   3. Updates the BIG visible price (handles Symmetry overwriting on variant change)
//   4. Attaches `properties[_Carat]` to the cart form so the Function picks it up
//      (underscore keeps it off the customer-facing checkout line; the checkout
//      spec sticker renders it instead)
//
// On variant change (metal / size), Symmetry rewrites the price element with
// the new variant's base price. We watch that element with a MutationObserver
// and, if the customer has a non-base carat selected, re-apply the adjustment.
// A guard flag prevents an infinite loop between our own writes and the MO.

console.log('[ND carat selector] script loaded');

(function () {
  'use strict';
  var root = document.querySelector('.nd-carat');
  if (!root) return;

  var baseCarat = parseFloat(root.dataset.baseCarat) || 2;
  var basePriceCents = parseFloat(root.dataset.basePriceCents) || 0;
  var stepUpUSD = parseFloat(root.dataset.stepUp) || 1100;
  var stepDownUSD = parseFloat(root.dataset.stepDown) || 800;
  var currency = root.dataset.currency || 'USD';
  // Fancy color diamonds (Blue / Yellow / Pink) follow a different price curve.
  // Set in Liquid via product.tags contains "All Colored Diamonds".
  var isFancy = root.dataset.isFancy === 'true';

  var pills = root.querySelectorAll('.nd-carat__pill');
  var noteText = root.querySelector('.nd-carat__note-text');

  // Internal flag: when true, our own MutationObserver-triggered writes are in flight
  var ourWrite = false;          // legacy flag (kept for any external readers)
  var lastOurWrite = null;       // last formatted text we wrote — content guard

  // ---- Helpers ----------------------------------------------------------
  function fmtMoney(cents) {
    var amount = Math.round(cents) / 100;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: currency,
        minimumFractionDigits: 2, maximumFractionDigits: 2
      }).format(amount);
    } catch (_) {
      return '$' + amount.toLocaleString('en-US', {
        minimumFractionDigits: 2, maximumFractionDigits: 2
      });
    }
  }
  function fmtCarat(n) {
    return Number.isInteger(n) ? String(n) : String(n);
  }
  // Piecewise pricing — rate switches as we walk from base to target.
  // Mirrors the Cart Transform Function on the server side.
  //
  // WHITE diamonds (default — boundary at 5/6):
  //   UP,   current < 5 ct  →  +$1,100/ct  (paid as $550 per 0.5 ct)
  //   UP,   current ≥ 5 ct  →  +$1,550/ct
  //   DOWN, current > 5 ct  →  −$1,100/ct
  //   DOWN, current ≤ 5 ct  →  −$800/ct   (paid as $400 per 0.5 ct)
  //
  // FANCY COLOR diamonds (boundaries at 3 and 5):
  //   UP,   current < 3 ct  →  +$1,600/ct  (paid as $800 per 0.5 ct)
  //   UP,   current < 5 ct  →  +$2,200/ct  (paid as $1,100 per 0.5 ct)
  //   UP,   current ≥ 5 ct  →  +$3,000/ct  (5→6 jump)
  //   DOWN, current > 5 ct  →  −$3,000/ct
  //   DOWN, current > 3 ct  →  −$1,400/ct  (paid as $700 per 0.5 ct)
  //   DOWN, current ≤ 3 ct  →  −$1,000/ct  (paid as $500 per 0.5 ct)
  function _round3(n) { return Math.round(n * 1000) / 1000; }

  function calcWhiteUSD(target) {
    var totalUSD = 0;
    var cur = baseCarat;
    if (target > baseCarat) {
      while (cur < target) {
        var stepSize, rate;
        if (cur < 5) {
          stepSize = Math.min(0.5, _round3(target - cur));
          rate = 1100;
        } else {
          stepSize = Math.min(1, _round3(target - cur));
          rate = 1550;
        }
        totalUSD += stepSize * rate;
        cur = _round3(cur + stepSize);
      }
    } else {
      while (cur > target) {
        var stepSize, rate;
        if (cur > 5) {
          stepSize = Math.min(1, _round3(cur - target));
          rate = 1100;
        } else {
          stepSize = Math.min(0.5, _round3(cur - target));
          rate = 800;
        }
        totalUSD -= stepSize * rate;
        cur = _round3(cur - stepSize);
      }
    }
    return totalUSD;
  }

  function calcFancyUSD(target) {
    var totalUSD = 0;
    var cur = baseCarat;
    if (target > baseCarat) {
      while (cur < target) {
        var stepSize, rate;
        if (cur < 3) {
          stepSize = Math.min(0.5, _round3(target - cur));
          rate = 1600;
        } else if (cur < 5) {
          stepSize = Math.min(0.5, _round3(target - cur));
          rate = 2200;
        } else {
          stepSize = Math.min(1, _round3(target - cur));
          rate = 3000;
        }
        totalUSD += stepSize * rate;
        cur = _round3(cur + stepSize);
      }
    } else {
      while (cur > target) {
        var stepSize, rate;
        if (cur > 5) {
          stepSize = Math.min(1, _round3(cur - target));
          rate = 3000;
        } else if (cur > 3) {
          stepSize = Math.min(0.5, _round3(cur - target));
          rate = 1400;
        } else {
          stepSize = Math.min(0.5, _round3(cur - target));
          rate = 1000;
        }
        totalUSD -= stepSize * rate;
        cur = _round3(cur - stepSize);
      }
    }
    return totalUSD;
  }

  // The walk is USD (the shop currency); basePriceCents is in the buyer's
  // presentment currency. Convert with the storefront rate and round to the
  // currency's minor unit, exactly as the cart Function charges it
  // (nd-carat-transform, 2026-10-21). USD (rate 1) is unchanged.
  var ZERO_DECIMAL = ['BIF', 'CLP', 'DJF', 'GNF', 'HUF', 'ISK', 'JPY', 'KMF', 'KRW',
    'PYG', 'RWF', 'TWD', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'];
  function usdToCents(usd) {
    var cur = (window.Shopify && window.Shopify.currency) || {};
    var rate = parseFloat(cur.rate);
    if (!(rate > 0) || rate === 1) return usd * 100;
    if (ZERO_DECIMAL.indexOf(cur.active) !== -1) return Math.round(usd * rate) * 100;
    return Math.round(usd * rate * 100);
  }

  function calcCents(carat) {
    var target = carat;
    if (target === baseCarat) return basePriceCents;
    var totalUSD = isFancy ? calcFancyUSD(target) : calcWhiteUSD(target);
    return basePriceCents + usdToCents(totalUSD);
  }
  function getCurrentSelectedCarat() {
    var pill = root.querySelector('.nd-carat__pill--selected');
    if (!pill) return baseCarat;
    var v = parseFloat(pill.dataset.carat);
    return Number.isFinite(v) ? v : baseCarat;
  }

  // ---- Spec table cell ---------------------------------------------------
  function updateSpecCarat(carat) {
    var el = document.querySelector('[data-spec-value="carat_weight"]');
    if (!el) return;
    el.textContent = fmtCarat(carat) + ' CARAT';
  }

  // ---- Live PDP price update --------------------------------------------
  // Selectors for the price elements Symmetry renders on the PDP
  var PRICE_SELECTORS = [
    '.price .price__current',
    '.price__default .price__current',
    '.price__sale .price__current',
    '.price-item--regular'
  ];

  // Only this product's own price elements. Recommendation cards ("You may
  // also like") render the same .price__current markup further down the page,
  // and a document-wide lookup used to overwrite their prices with this
  // product's carat price (customer report, 2026-09-24).
  var priceScope = root.closest('.product-info') || document;

  function getPriceEls() {
    var out = [];
    PRICE_SELECTORS.forEach(function (sel) {
      priceScope.querySelectorAll(sel).forEach(function (el) {
        if (el.closest('.product-block')) return;
        if (out.indexOf(el) === -1) out.push(el);
      });
    });
    return out;
  }

  function setPriceText(carat) {
    var newCents = calcCents(carat);
    var formatted = fmtMoney(newCents) + ' ' + currency;
    ourWrite = true;
    lastOurWrite = formatted.replace(/\s+/g, ' ').trim();
    getPriceEls().forEach(function (el) {
      el.textContent = formatted;
    });
    setTimeout(function () { ourWrite = false; }, 80);
  }

  // ---- Capture Symmetry's variant-change overwrites ----------------------
  // When metal/size changes, Symmetry rewrites .price__current with the new
  // variant's base price. We parse that, update our basePriceCents, and
  // re-apply the customer's currently-selected carat adjustment.
  function parseDollarsFromText(text) {
    if (!text) return null;
    // Match $X,XXX.YY or X,XXX.YY (any common money format)
    var m = text.replace(/\s/g, '').match(/[\$£€¥₪]?([\d,]+(?:\.\d+)?)/);
    if (!m) return null;
    return parseFloat(m[1].replace(/,/g, ''));
  }

  function watchPriceElements() {
    var els = getPriceEls();
    els.forEach(function (el) {
      if (el.__ndWatched) return;
      el.__ndWatched = true;
      var mo = new MutationObserver(function () {
        // Content-based guard: skip ONLY if the current text matches the
        // exact value we last wrote. The previous 80ms time-based guard
        // caused this bug — when pdp-variant-price-preview wrote AFTER our
        // own boost write (RAF-deferred), the time guard was still active
        // and we'd skip re-applying the boost, leaving the customer
        // looking at the base carat price.
        var currentText = (el.textContent || '').replace(/\s+/g, ' ').trim();
        if (lastOurWrite && currentText === lastOurWrite) return;

        // External write (Symmetry / variant-price-preview). Parse the
        // new variant's base price, re-apply the customer's carat delta.
        var dollars = parseDollarsFromText(currentText);
        if (dollars == null) return;
        basePriceCents = Math.round(dollars * 100);
        var current = getCurrentSelectedCarat();
        if (current !== baseCarat) {
          setPriceText(current);
        }
        updateShopPayInstallments(current);
      });
      mo.observe(el, { childList: true, characterData: true, subtree: true });
    });
  }

  // ---- Cart property wiring ---------------------------------------------
  function getProductForms() {
    return document.querySelectorAll(
      'form[action*="/cart/add"], product-form form, form[action$="/cart/add"]'
    );
  }
  function ensureCaratInput(form) {
    if (!form) return null;
    var existing = form.querySelector('input[name="properties[_Carat]"]');
    if (existing) return existing;
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[_Carat]';
    form.appendChild(input);
    return input;
  }
  function setCaratOnAllForms(carat) {
    getProductForms().forEach(function (form) {
      var input = ensureCaratInput(form);
      if (input) input.value = fmtCarat(carat);
    });
  }

  // ---- Shop Pay installment update ---------------------------------------
  // Shopify's <shopify-payment-terms> computes installment text from a
  // `shopify-meta` JSON attribute (full_price + price_per_term per variant).
  // It doesn't see our Carat property — so we patch the JSON ourselves so
  // installment math reflects the customer's carat selection, not the base.
  function fmtShopifyMoney(cents) {
    var dollars = Math.round(cents) / 100;
    return '$' + dollars.toLocaleString('en-US', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
  }
  function updateShopPayInstallments(carat) {
    var terms = document.querySelectorAll('shopify-payment-terms');
    if (!terms.length) return;
    var newCents = calcCents(carat);
    // Apply the same active discount % the storefront displays — so the
    // monthly installment matches the price the customer will actually be
    // charged at checkout, not the pre-sale retail price.
    var promo = window.ndPromo || {};
    var pct = parseFloat(promo.discountPercent);
    if (!isNaN(pct) && pct > 0 && pct < 100) {
      newCents = Math.round(newCents * (1 - pct / 100));
    }
    terms.forEach(function (sp) {
      var raw = sp.getAttribute('shopify-meta');
      if (!raw) return;
      var meta;
      try { meta = JSON.parse(raw); } catch (e) { return; }
      if (!meta || !Array.isArray(meta.variants)) return;
      var vid = parseInt(sp.getAttribute('variant-id'), 10);
      var v = meta.variants.find(function (x) { return x.id === vid; });
      if (!v) {
        meta.variants.forEach(function (x) {
          x.full_price = fmtShopifyMoney(newCents);
          if (x.number_of_payment_terms) {
            x.price_per_term = fmtShopifyMoney(newCents / x.number_of_payment_terms);
          }
        });
      } else {
        v.full_price = fmtShopifyMoney(newCents);
        if (v.number_of_payment_terms) {
          v.price_per_term = fmtShopifyMoney(newCents / v.number_of_payment_terms);
        }
      }
      sp.setAttribute('shopify-meta', JSON.stringify(meta));
      var cur = sp.getAttribute('variant-id');
      if (cur) sp.setAttribute('variant-id', cur);
    });
  }

  // ---- Pill click --------------------------------------------------------
  function select(carat) {
    pills.forEach(function (p) {
      var v = parseFloat(p.dataset.carat);
      var sel = v === carat;
      p.classList.toggle('nd-carat__pill--selected', sel);
      p.setAttribute('aria-checked', String(sel));
    });

    if (noteText) {
      if (carat === baseCarat) {
        noteText.innerHTML = 'Photos show a <strong>' + fmtCarat(baseCarat) + '</strong> ct stone.';
      } else {
        noteText.innerHTML = 'Photos show a <strong>' + fmtCarat(baseCarat) +
          '</strong> ct stone, you selected <strong>' +
          fmtCarat(carat) + '</strong> carat.';
      }
    }

    updateSpecCarat(carat);
    setPriceText(carat);
    setCaratOnAllForms(carat);
    updateShopPayInstallments(carat);
  }

  // ---- Init --------------------------------------------------------------
  setCaratOnAllForms(baseCarat);
  watchPriceElements();
  // Fire Shop Pay installment sync at the baseline carat so the monthly text
  // reflects the discounted price (not the pre-discount retail) on first load.
  // Slight delay so <shopify-payment-terms> has hydrated first.
  setTimeout(function () { updateShopPayInstallments(baseCarat); }, 600);
  setTimeout(function () { updateShopPayInstallments(baseCarat); }, 1800);

  pills.forEach(function (p) {
    p.addEventListener('click', function (e) {
      e.preventDefault();
      var c = parseFloat(p.dataset.carat);
      if (!isNaN(c)) select(c);
    });
  });

  // Late-arriving price elements: rescan a couple of seconds in case Symmetry
  // mounts them after our initial run
  setTimeout(watchPriceElements, 500);
  setTimeout(watchPriceElements, 1500);
})();
